/* eslint-disable react-refresh/only-export-components -- component with helper functions and type exports */
/**
 * ActionRenderer - Main dispatch component for rendering actions.
 *
 * Uses the action registry to look up the appropriate renderer
 * for each action type and passes through standardized props.
 *
 * @example
 * ```tsx
 * // Full editable mode
 * <ActionRenderer
 *   action={action}
 *   mode="full"
 *   onUpdate={handleUpdate}
 *   proposalId={123}
 *   orgId={456}
 * />
 *
 * // Header mode for accordion
 * <ActionRenderer action={action} mode="header" />
 *
 * // Compact mode for lists
 * <ActionRenderer action={action} mode="compact" />
 * ```
 */

import { useCrmPreview } from '@/lib/crm/preview';

import type {
  ProposedAction,
  SourceContext,
} from '../../lib/proposals/core/types';
import type {
  ActionRendererProps,
  RenderMode,
} from '../../lib/proposals/registry';
import { getActionMetadata, lookupAction } from '../../lib/proposals/registry';
import { ActionEmpty, ActionError } from './shared';

// Re-export types and utilities for convenience
export type { ActionRendererProps, RenderMode };
export { getActionMetadata };

interface ActionRendererComponentProps {
  /** The action to render */
  action: ProposedAction | null | undefined;
  /** The render mode */
  mode: RenderMode;
  /** Callback to update the action (only for editable modes) */
  onUpdate?: (updatedAction: ProposedAction) => void;
  /** Additional CSS classes */
  className?: string;
  /** Proposal ID (required for editable actions) */
  proposalId?: number;
  /** Organization ID (required for editable actions) */
  orgId?: number;
  /** Whether the action is currently being processed */
  isProcessing?: boolean;
  /** Callback when user clicks "View Thread" (for email actions) */
  onOpenThread?: () => void;
  /** Number of versions for version history button */
  versionCount?: number;
  /** Callback when version history button is clicked */
  onShowVersionHistory?: () => void;
  /** Proposal source type (e.g., "ahoy_background_agent") */
  sourceTypeName?: string;
  /** Proposal source context (channel, counterparty, etc.) */
  sourceContext?: SourceContext | null;
  /** True when this action is the proposal's ONLY action */
  isOnlyAction?: boolean;
}

/**
 * Main action renderer component.
 *
 * Dispatches to the appropriate renderer based on action type,
 * using the action registry for lookup.
 */
export function ActionRenderer({
  action,
  mode,
  onUpdate,
  className = '',
  proposalId,
  orgId,
  isProcessing = false,
  onOpenThread,
  versionCount,
  onShowVersionHistory,
  sourceTypeName,
  sourceContext,
  isOnlyAction,
}: ActionRendererComponentProps) {
  // Threaded down to render functions in registry.helpers.tsx via
  // ActionRendererProps.preview — those are plain functions, not components,
  // and cannot call hooks themselves. `null` outside a CrmPreviewProvider.
  const preview = useCrmPreview();

  // Handle null/undefined action
  if (!action) {
    return <ActionEmpty mode={mode} className={className} />;
  }

  // Look up registry entry
  const entry = lookupAction(action);

  // Handle unknown action type
  if (!entry) {
    return (
      <ActionError
        message={`Unknown action type: ${action.proposed_action_type_name}`}
        mode={mode}
        className={className}
      />
    );
  }

  // Handle mode-specific rendering
  const title = entry.getTitle(action.payload, action);
  const Icon = entry.icon;

  // Inline mode - text only
  if (mode === 'inline') {
    let colorClass = '';
    if (entry.colorScheme === 'destructive') {
      colorClass = 'text-error-600';
    } else if (entry.colorScheme === 'warning') {
      colorClass = 'text-warning-600';
    }
    const ariaLabel =
      entry.colorScheme === 'destructive' || entry.colorScheme === 'warning'
        ? title
        : undefined;

    return (
      <span className={`${colorClass} ${className}`} aria-label={ariaLabel}>
        {title}
      </span>
    );
  }

  // Compact mode - custom renderer or default icon + text
  if (mode === 'compact') {
    if (entry.renderCompact) {
      return entry.renderCompact({
        action,
        mode,
        className,
        proposalId,
        orgId,
        isProcessing,
        enrichedData: action.enriched_data ?? undefined,
        onOpenThread,
        versionCount,
        onShowVersionHistory,
        preview,
      });
    }

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Icon className='w-4 h-4 shrink-0 text-fg-secondary' />
        <span className='text-sm truncate'>{title}</span>
      </div>
    );
  }

  // Header mode - icon + title + category badge
  if (mode === 'header') {
    const iconColor = '';
    let badgeBgColor = 'bg-primary-100';
    let badgeTextColor = 'text-primary-700';
    if (entry.colorScheme === 'destructive') {
      badgeBgColor = 'bg-error-100';
      badgeTextColor = 'text-error-700';
    } else if (entry.colorScheme === 'warning') {
      badgeBgColor = 'bg-warning-100';
      badgeTextColor = 'text-warning-700';
    }

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <span className='font-medium text-fg-primary truncate'>{title}</span>
        <span
          className={`text-xs px-2 py-0.5 rounded-full ${badgeBgColor} ${badgeTextColor}`}
        >
          {entry.badgeLabel ?? entry.category}
        </span>
      </div>
    );
  }

  const Component = entry.component;

  // Determine if editing should be allowed
  const shouldAllowEdit =
    entry.supportsEditing && mode === 'full' && !!onUpdate;

  // Build props for the renderer
  const rendererProps: ActionRendererProps = {
    action,
    mode,
    onUpdate: shouldAllowEdit ? onUpdate : undefined,
    className,
    proposalId,
    orgId,
    isProcessing,
    enrichedData: action.enriched_data ?? undefined,
    onOpenThread,
    versionCount,
    onShowVersionHistory,
    sourceTypeName,
    sourceContext,
    preview,
    isOnlyAction,
  };

  return <Component {...rendererProps} />;
}

/**
 * Get the icon component for an action.
 * Convenience function for use in headers/lists.
 */
export function ActionIcon({
  action,
  className = 'w-5 h-5',
}: {
  action: ProposedAction;
  className?: string;
}) {
  const metadata = getActionMetadata(action);
  if (!metadata) {
    return null;
  }

  const Icon = metadata.icon;
  return <Icon className={className} />;
}

/**
 * Get the title for an action.
 * Convenience function for use in headers/lists.
 */
export function getActionTitle(action: ProposedAction): string {
  const metadata = getActionMetadata(action);
  return metadata?.title ?? action.proposed_action_type_name;
}

/**
 * Get the category for an action.
 * Convenience function for filtering.
 */
export function getActionCategory(action: ProposedAction) {
  const metadata = getActionMetadata(action);
  return metadata?.category ?? null;
}

/**
 * Check if an action is destructive (delete or merge).
 */
export function isDestructiveAction(action: ProposedAction): boolean {
  const metadata = getActionMetadata(action);
  return metadata?.isDestructive ?? false;
}
